import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import CheckCircle from '@mui/icons-material/CheckCircle'
import CardHeader from '@mui/material/CardHeader';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';

import moment from 'moment'
import { SignalItem } from '../../interface/SignalItem';
import { CardActionArea } from '@mui/material';
import { blue } from '@mui/material/colors';
import { StepsSection } from './StepsSection';
import { BacktestingSection } from './BacktestingSection';
import { dashboardMutedTextSx, getDashboardPanelSx } from '../dashboardv2/dashboardStyles';

interface SignalCardProps
{
  signal: SignalItem
  isEnabled: boolean
  onClickSignal: (signal: SignalItem) => void
}

export const SignalCard = ({ signal, isEnabled, onClickSignal }: SignalCardProps) =>
{
  const theme = useTheme()

  const isSignalExpired = () =>
  {
    if(signal.timeout < new Date().valueOf()) return true
    return false
  }

  const getBadgeColor = () =>
  {
    const price = Number(signal.currentPrice)

    if(signal.direction === "LONG")
    {
      if(price > signal.entry) return "success"
      if(price < signal.entry) return "error"
    }

    if(signal.direction === "SHORT")
    {
      if(price > signal.entry) return "error"
      if(price < signal.entry) return "success"
    }

    return "default"
  }

  const getDirectionColor = () =>
  {
    if(signal.direction === "LONG") return "success"
    if(signal.direction === "SHORT") return "secondary"
  }

  const cardSx = isSignalExpired()
    ? {
        ...getDashboardPanelSx(theme, 310),
        backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.55),
        opacity: 0.84,
      }
    : {
        ...getDashboardPanelSx(theme, 310),
      }


  return(
    <Grid item xs={12} md={12} lg={6} xl={6}>
    <CardActionArea onClick={() => onClickSignal(signal)}>
      <Card sx={{ ...cardSx, p: 2.5 }}>
        <Stack spacing={2} sx={{ position: "relative", zIndex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Badge badgeContent={`${signal.percentIncrease}%`} color={getBadgeColor()} >
                <Avatar sx={{ bgcolor: blue[500], height: 58, width: 58, fontWeight: 700 }} aria-label="recipe">
                  {signal.pair[0]}
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 1.4, ...dashboardMutedTextSx(theme) }}>
                  {signal.source}
                </Typography>
                <Typography sx={{ fontSize: 24, lineHeight: 1.1 }}>
                  {signal.coin}
                </Typography>
                <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>
                  {signal.description || "No coin info"}
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={1} alignItems="flex-end">
              {isEnabled && <Tooltip title="This source is enabled"><CheckCircle htmlColor="#66BB6A" /></Tooltip>}
              <Chip label={signal.direction} color={getDirectionColor()} sx={{ borderRadius: "999px" }} />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Base ${signal.base}`} sx={styles.metaChip(theme)} />
            <Chip label={`Pair ${signal.pair}`} sx={styles.metaChip(theme)} />
            <Chip label={isSignalExpired() ? "Expired" : `Timeout ${moment(signal.timeout).fromNow()}`} sx={styles.metaChip(theme)} />
          </Stack>

          { signal?.backtest && (
            <Box sx={styles.infoBlock(theme)}>
              <BacktestingSection signal={signal} />
            </Box>
          )}

          <Box sx={styles.infoBlock(theme)}>
            <Typography gutterBottom variant="caption" component="div" sx={dashboardMutedTextSx(theme)}>
              {isSignalExpired() && `Signal has expired | `} Created { moment(signal.time_generated).fromNow() }
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Entry {Number(signal.entry).toFixed(8)} | Current {Number(signal.currentPrice || 0).toFixed(8)} | Timeout { moment(signal.timeout).format('DD/MM/YY h:mmA')}
            </Typography>
          </Box>

          <Box sx={styles.stepperWrap(theme)}>
            <StepsSection signal={signal}/>
          </Box>
        </Stack>
        
      </Card>
    </CardActionArea>
    </Grid>
  )
}

const styles = {
  metaChip: (theme: any) => ({
    borderRadius: "999px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.68),
    color: theme.palette.text.primary,
  }),
  infoBlock: (theme: any) => ({
    p: 1.5,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.12)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.52),
  }),
  stepperWrap: (theme: any) => ({
    p: 1.5,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.12)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.52),
  }),
}
